import axios, { AxiosResponse } from 'axios'
import {
  MatchingPaymentTicketListResponse,
  MatchingPayoutTicketListResponse,
  MatchingPaymentFinishedTicketsRequestParams,
  MatchingPayoutFinishedTicketsRequestParams,
  TicketActionResponse,
  VirtualMerchantPaymentResponse,
  MatchingFile,
  MatchingFileResponse,
  MatchingSummaryResponse,
  MatchingPayoutRequisite,
  PayoutRequisiteOption,
  ReplacePayoutRequisiteBody,
} from './_models'
import { ApiResponse } from '../../../common/api/response.types'
import { API_URL } from '../../../../constants'
import {
  mockGetMatchingPaymentTickets,
  mockGetMatchingPayoutTickets,
  mockVerifyMatchingPaymentBill,
  mockRejectMatchingPaymentBill,
  mockHaltMatchingPayoutTicket,
  mockDeclineMatchingPayoutTicket,
  mockFinishMatchingPaymentTicket,
  mockGetMerchantList,
  mockChangeExpectedAmountMatchingPaymentTicket,
} from './_mocks'
import { getListMerchantsForSelect } from '../../../common/core/_requests'
import { getUserRolesByHierarchy, ROLE, UserModel } from '../../auth'
import { Merchant } from '../../../common/core/_models'

const USE_MOCKS = false

// Пополнения
export const GET_MATCHING_PAYMENT_TICKETS = `${API_URL}/api/matching-ticket/payment/list`
export const COMPLETE_PAYMENT_TICKET = `${API_URL}/api/payment/complete`
export const COMPLETE_PAYMENT_TICKET_INTERNAL = `${API_URL}/api/internal/payment/complete`
export const DECLINE_PAYMENT_TICKET = `${API_URL}/api/payment/decline`
export const DECLINE_PAYMENT_TICKET_INTERNAL = `${API_URL}/api/internal/payment/decline`
export const CHANGE_EXPECTED_AMOUNT_PAYMENT_TICKET = `${API_URL}/api/payment/change-expected-amount`
export const CHANGE_EXPECTED_AMOUNT_PAYMENT_TICKET_INTERNAL = `${API_URL}/api/internal/payment/change-expected-amount`

// Выплаты
export const GET_MATCHING_PAYOUT_TICKETS = `${API_URL}/api/matching-ticket/payout/list`
export const PAYOUT_REQUISITE_BASE = `${API_URL}/api/matching-ticket/payout`
export const MATCHING_DEPOSIT_BASE = `${API_URL}/api/matching-ticket/deposit`
export const HALT_PAYOUT_TICKET = `${API_URL}/api/payout/halt`
export const CREATE_VIRTUAL_MERCHANT_PAYMENT = `${API_URL}/api/virtual-merchant/create-payment`
export const GET_MATCHING_SUMMARY = `${API_URL}/api/internal/matching/summary`

// Виртуальный мерчант
export const GET_VIRTUAL_MERCHANT_TICKETS = `${API_URL}/api/virtual-merchant/tickets/list`

// Операции с чеками
export const VERIFY_PAYMENT_BILL = `${API_URL}/api/payment/bill`
export const REJECT_PAYMENT_BILL = `${API_URL}/api/payment/bill`
export const ADD_PAYMENT_BILL = `${API_URL}/api/payment/bill`
export const REPLACE_PAYMENT_RECEIPT = `${API_URL}/api/internal/payment/receipt`

// Получить список тикетов пополнения (matching)
export function getMatchingPaymentTickets(
  params: Partial<MatchingPaymentFinishedTicketsRequestParams>
) {
  if (USE_MOCKS) {
    return mockGetMatchingPaymentTickets(params)
  }
  return axios.get<MatchingPaymentTicketListResponse>(GET_MATCHING_PAYMENT_TICKETS, {
    params,
  })
}

// Получить список тикетов выплат (matching)
export function getMatchingPayoutTickets(params: Partial<MatchingPayoutFinishedTicketsRequestParams>) {
  if (USE_MOCKS) {
    return mockGetMatchingPayoutTickets(params)
  }
  return axios.get<MatchingPayoutTicketListResponse>(GET_MATCHING_PAYOUT_TICKETS, {
    params,
  })
}

// Общая сводка матчинга (сейчас — суммарный остаток по активным выплатам).
// merchantTokenList / paymentMethodList — опциональные фильтры (те же значения, что у списка
// выплат); складываются по AND, без них — глобальная сводка.
export function getMatchingSummary(params?: {
  merchantTokenList?: string[]
  paymentMethodList?: string[]
}) {
  return axios.get<MatchingSummaryResponse>(GET_MATCHING_SUMMARY, { params })
}

// Подтвердить чек (проверен)
export async function verifyMatchingPaymentBill(id: string) {
  if (USE_MOCKS) {
    return mockVerifyMatchingPaymentBill(id)
  }
  return axios.post(`${VERIFY_PAYMENT_BILL}/${id}/verify`)
}

// Отклонить чек (забракован)
export async function rejectMatchingPaymentBill(id: string) {
  if (USE_MOCKS) {
    return mockRejectMatchingPaymentBill(id)
  }
  return axios.post(`${REJECT_PAYMENT_BILL}/${id}/rejected`)
}

// Завершить тикет платежа
export function finishMatchingPaymentTicket(
  token: string,
  currentUser: UserModel | undefined,
  externalUser?: string,
  amount?: number
): Promise<AxiosResponse<TicketActionResponse>> {
  if (USE_MOCKS) {
    return new Promise((resolve) =>
      resolve(
        mockFinishMatchingPaymentTicket(
          token,
          amount
        ) as unknown as AxiosResponse<TicketActionResponse>
      )
    )
  }

  const userRoles = currentUser ? getUserRolesByHierarchy(currentUser) : []
  const endpoint =
    userRoles.includes(ROLE.ROLE_SENIOR_OPERATOR) || userRoles.includes(ROLE.ROLE_OPERATOR) || userRoles.includes(ROLE.ROLE_MATCHING_OPERATOR) || userRoles.includes(ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR) || userRoles.includes(ROLE.ROLE_MERCHANT_MANAGER)
      ? COMPLETE_PAYMENT_TICKET
      : COMPLETE_PAYMENT_TICKET_INTERNAL

  const data = {
    ...(externalUser !== undefined ? { externalUserId: externalUser } : {}),
    ...(amount !== undefined ? { amount: amount.toString() } : {}),
  }

  return Object.keys(data).length
    ? axios.post<TicketActionResponse>(`${endpoint}/${token}`, data)
    : axios.post<TicketActionResponse>(`${endpoint}/${token}`)
}

// Отклонить тикет платежа (просроченное)
export function declineMatchingPaymentTicket(
  token: string,
  currentUser: UserModel | undefined
): Promise<AxiosResponse<TicketActionResponse>> {
  if (USE_MOCKS) {
    return new Promise((resolve) =>
      resolve(
        mockDeclineMatchingPayoutTicket(token) as unknown as AxiosResponse<TicketActionResponse>
      )
    )
  }
  const userRoles = currentUser ? getUserRolesByHierarchy(currentUser) : []
  const endpoint =
    userRoles.includes(ROLE.ROLE_SENIOR_OPERATOR) || userRoles.includes(ROLE.ROLE_OPERATOR) || userRoles.includes(ROLE.ROLE_MATCHING_OPERATOR) || userRoles.includes(ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR) || userRoles.includes(ROLE.ROLE_MERCHANT_MANAGER)
      ? DECLINE_PAYMENT_TICKET
      : DECLINE_PAYMENT_TICKET_INTERNAL

  return axios.get(`${endpoint}/${token}`)
}

// Остановить (отменить) тикет выплаты
export function partiallyFinishMatchingPayoutTicket(token: string) {
  const body = { manualRecreation: true }
  if (USE_MOCKS) {
    return mockHaltMatchingPayoutTicket(token, body)
  }
  return axios.post(`${HALT_PAYOUT_TICKET}/${token}`, body)
}

// Поставить выплату в мэтчинге на паузу: новые пополнения на неё не назначаются
export function pauseMatchingPayout(token: string) {
  return axios.post(`${PAYOUT_REQUISITE_BASE}/${token}/pause`)
}

// Снять выплату с паузы
export function resumeMatchingPayout(token: string) {
  return axios.post(`${PAYOUT_REQUISITE_BASE}/${token}/resume`)
}

// Отклонить тикет выплаты (альтернативный endpoint)
export function declineMatchingPayoutTicket(token: string) {
  const body = { manualRecreation: false }
  if (USE_MOCKS) {
    return mockDeclineMatchingPayoutTicket(token)
  }
  return axios.post(`${HALT_PAYOUT_TICKET}/${token}`, body)
}

export async function getMerchantList(currentUser?: UserModel): Promise<Merchant[]> {
  if (USE_MOCKS) {
    return (await mockGetMerchantList()).data.merchants
  }
  return (await getListMerchantsForSelect(currentUser))?.data?.merchants || []
}

// Создать платеж виртуального мерчанта
export function createVirtualMerchantPayment(
  payoutTicketToken: string
): Promise<AxiosResponse<VirtualMerchantPaymentResponse>> {
  return axios.post<VirtualMerchantPaymentResponse>(CREATE_VIRTUAL_MERCHANT_PAYMENT, {
    payoutTicketToken,
  })
}

// Получить список тикетов виртуального мерчанта
export type VirtualMerchantSearchField =
  | 'matchingTicketToken'
  | 'paymentTicketToken'
  | 'virtualPayoutTicketToken'

export function getVirtualMerchantTickets(
  params: {
    page: number
    perPage: number
    sortBy?: 'createdAt'
    sortDirection?: 'ASC' | 'DESC'
  } & Partial<Record<VirtualMerchantSearchField, string>>
) {
  return axios.get<{
    success: boolean
    totalRows: number
    list: Array<{
      paymentTicketToken: string
      matchingTicketToken: string
      virtualPayoutTicketToken: string
      amount: number
      createdAt: string
    }>
  }>(GET_VIRTUAL_MERCHANT_TICKETS, { params })
}

// Прикрепление чека
export function addBillMatchingPaymentTicket(
  token: string,
  bill?: File
): Promise<AxiosResponse<MatchingFileResponse>> {
  const formData = new FormData()
  if (bill) {
    formData.append('file', bill)
  }

  return axios.post(`${ADD_PAYMENT_BILL}/${token}`, formData)
}

// Замена чека пополнения сотрудником: прежний файл остаётся в истории
export function replaceMatchingPaymentReceipt(token: string, file: File) {
  const formData = new FormData()
  formData.append('file', file)

  return axios.post<ApiResponse<{ files: MatchingFile[] }>>(`${REPLACE_PAYMENT_RECEIPT}/${token}/replace`, formData)
}

// Изменение запрошенной суммы
export function ChangeExpectedAmountMatchingPaymentTicket(
  token: string,
  amount: number,
  currentUser: UserModel | undefined,
  externalUser?: string,
): Promise<AxiosResponse<TicketActionResponse>> {
  if (USE_MOCKS) {
    return new Promise((resolve) =>
      resolve(
        mockChangeExpectedAmountMatchingPaymentTicket(
          token,
          amount
        ) as unknown as AxiosResponse<TicketActionResponse>
      )
    )
  }

  const userRoles = currentUser ? getUserRolesByHierarchy(currentUser) : []
  const endpoint =
    userRoles.includes(ROLE.ROLE_SENIOR_OPERATOR) || userRoles.includes(ROLE.ROLE_OPERATOR) || userRoles.includes(ROLE.ROLE_MATCHING_OPERATOR) || userRoles.includes(ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR) || userRoles.includes(ROLE.ROLE_MERCHANT_MANAGER)
      ? CHANGE_EXPECTED_AMOUNT_PAYMENT_TICKET
      : CHANGE_EXPECTED_AMOUNT_PAYMENT_TICKET_INTERNAL

  return axios.post<TicketActionResponse>(`${endpoint}/${token}`, { amount: amount })
}

// Реквизиты клиента, которыми можно заменить канал выплаты (CPAYMENT-1733).
// Бэкенд сам сужает выборку платёжным методом и валютой заменяемого канала.
export function getPayoutRequisiteOptions(payoutToken: string, channelToken: string) {
  return axios.get<ApiResponse<PayoutRequisiteOption[]>>(
    `${PAYOUT_REQUISITE_BASE}/${payoutToken}/requisite/options`,
    { params: { channelToken } }
  )
}

// Заменить канал выплаты: платёжка правит связку и копии первичного канала.
export function replacePayoutRequisite(payoutToken: string, body: ReplacePayoutRequisiteBody) {
  return axios.post<ApiResponse<MatchingPayoutRequisite>>(
    `${PAYOUT_REQUISITE_BASE}/${payoutToken}/requisite/replace`,
    body
  )
}

// Пересобрать замороженный конфиг выплаты в мэтчинге. Идемпотентно — при сбое можно повторить,
// реквизит к этому моменту уже заменён и новые депозиты получают его.
export function rebuildPayoutSnapshot(payoutToken: string) {
  return axios.post<ApiResponse<null>>(
    `${PAYOUT_REQUISITE_BASE}/${payoutToken}/requisite/rebuild-snapshot`
  )
}

// Освободить выплату, которую держит спорный депозит (CPAYMENT-1857). `at` — момент, с которого
// удержание снимается; по умолчанию окно подставляет текущее время. Решение принимает мэтчинг:
// `released: false` означает «освобождать было нечего» и ошибкой не является.
export function releaseDepositChunk(depositToken: string, at: string) {
  return axios.post<ApiResponse<null> & { released?: boolean }>(
    `${MATCHING_DEPOSIT_BASE}/${depositToken}/chunk/release`,
    { at }
  )
}
